/**
 * Cloudflare Turnstile CAPTCHA Widget
 *
 * Setup:
 * 1. Create a Turnstile widget at https://dash.cloudflare.com/turnstile
 * 2. Add the site key to .env: EXPO_PUBLIC_TURNSTILE_SITE_KEY=your-site-key
 * 3. Add the secret key to Supabase: supabase secrets set TURNSTILE_SECRET_KEY=your-secret-key
 *
 * For development, use Cloudflare's test keys:
 * - Site key (always passes): 1x00000000000000000000AA
 * - Secret key (always passes): 1x0000000000000000000000000000000AA
 *
 * The widget only renders on web. Native clients skip CAPTCHA and rely on
 * server-side rate limiting for bot protection.
 */

import { useEffect, useRef, useState } from 'react';
import { View, Text, Platform } from 'react-native';
import { useThemeColors } from '@/lib/useThemeColors';

// ============================================================================
// Types
// ============================================================================

interface TurnstileWidgetProps {
  siteKey: string;
  onVerify: (token: string) => void;
  onError?: () => void;
  onExpire?: () => void;
  /** Widget theme */
  theme?: 'light' | 'dark' | 'auto';
  /** Widget size */
  size?: 'normal' | 'compact' | 'invisible';
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Turnstile site key from environment.
 * Set EXPO_PUBLIC_TURNSTILE_SITE_KEY in .env.
 * For development, use Cloudflare's test key: 1x00000000000000000000AA
 */
export const TURNSTILE_SITE_KEY = process.env.EXPO_PUBLIC_TURNSTILE_SITE_KEY || '';

// ============================================================================
// Helpers
// ============================================================================

/**
 * Reset a Turnstile widget by its widget ID.
 * Call this when the form is reset or after an error to present a fresh challenge.
 */
export function resetTurnstile(widgetIdRef: string | null): void {
  if (Platform.OS === 'web' && widgetIdRef && (window as any).turnstile) {
    try {
      (window as any).turnstile.reset(widgetIdRef);
    } catch {
      // Widget may have already been removed
    }
  }
}

// ============================================================================
// Component
// ============================================================================

export function TurnstileWidget({
  siteKey,
  onVerify,
  onError,
  onExpire,
  theme = 'auto',
  size = 'normal',
}: TurnstileWidgetProps) {
  const colors = useThemeColors();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);
  const [isReady, setIsReady] = useState<boolean>(false);
  const isNative = Platform.OS !== 'web';

  // Load the Turnstile script (web only — no-op on native)
  useEffect(() => {
    if (isNative) return;
    // Check if already loaded
    if ((window as any).turnstile) {
      setIsReady(true);
      return;
    }

    // Load the Turnstile script from Cloudflare CDN
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad';
    script.async = true;
    script.defer = true;

    // Callback when script loads
    (window as any).onTurnstileLoad = () => {
      setIsReady(true);
    };

    document.head.appendChild(script);

    return () => {
      // Cleanup the global callback
      delete (window as any).onTurnstileLoad;
    };
  }, []);

  // Render the widget once the script is loaded (web only)
  useEffect(() => {
    if (isNative || !isReady || !containerRef.current) return;

    // Reset if already rendered
    if (widgetId.current) {
      (window as any).turnstile.reset(widgetId.current);
      return;
    }

    widgetId.current = (window as any).turnstile.render(containerRef.current, {
      sitekey: siteKey,
      theme,
      size,
      callback: onVerify,
      'error-callback': onError,
      'expired-callback': onExpire,
    });

    return () => {
      if (widgetId.current) {
        try {
          (window as any).turnstile.remove(widgetId.current);
        } catch {
          // Widget might already be removed
        }
        widgetId.current = null;
      }
    };
  }, [isNative, isReady, siteKey]);

  // On native, Turnstile is not available — skip widget
  if (isNative) {
    return null; // Native submissions rely on rate limiting only
  }

  return (
    <View>
      <div ref={containerRef} style={{ minHeight: size === 'invisible' ? 0 : 65 }} />
      {!isReady && (
        <Text
          style={{ color: colors.text.tertiary }}
          className="text-xs text-center"
        >
          Loading security check...
        </Text>
      )}
    </View>
  );
}
